import React, { useState, useEffect, useContext, useCallback } from "react";
import moment from "moment";
import ListGroup from "../components/common/listGroup";
import Loading from "../components/common/loading";
import LiveNavbar from "../components/LiveNavbar";
import LiveChart from "../components/LiveChart";
import DataTable from "../components/LiveTable";
import UserContext from "../context/userContext";
import SidebarContext from "../context/sidebarContext";
import http from "../services/http";
import ParameterSubscribe from "../components/ParameterSubscribe";
import SelectDate from "../components/common/selectDate";
import { DragDropContext, Droppable } from "react-beautiful-dnd";

const sites = [
    { name: "Bardney", id: "BA" },
    { name: "Bury", id: "BU" },
    { name: "Cantley", id: "CA" },
    { name: "Newark", id: "NE" },
    { name: "Wissington", id: "WI" },
];

const dateRanges = [
    { name: "1D", id: "1" },
    { name: "2D", id: "2" },
    { name: "3D", id: "3" },
    { name: "1W", id: "7" },
];

const defaultHourlyDateRange = {
    name: "1D",
    id: "1",
};
const defaultDailyDateRange = {
    name: "1W",
    id: "7",
};

const dailyDateRanges = [
    { name: "1W", id: "7" },
    { name: "2W", id: "14" },
    { name: "3W", id: "21" },
];

const LiveData = () => {
    const [user] = useContext(UserContext);
    const [sidebar] = useContext(SidebarContext);

    const [screens, setScreens] = useState([]);
    // const [operation, setOperation] = useState("BC");
    const [activeSite, setActiveSite] = useState({});
    const [activeScreen, setActiveScreen] = useState({});
    const [activeDateRange, setActiveDateRange] = useState(
        defaultHourlyDateRange
    );
    const [date, setDate] = useState(moment());
    const [asc, setAsc] = useState(false);

    // ?? Load cached order when user changes
    useEffect(() => {
        if (user) {
            if (user.SITE !== "CO") setActiveSite({ id: user.SITE });

            const cachedOrder = localStorage.getItem(`screensOrder_${user.ID}`);
            if (cachedOrder) {
                setScreens(JSON.parse(cachedOrder));
            }
        }
    }, [user]);

    useEffect(() => {
        // Update date every minute
        let timerId;
        timerId = setInterval(() => {
            setDate(moment());
        }, 60 * 1000);
        return () => {
            clearInterval(timerId);
        };
    }, []);

    useEffect(() => {
        async function getScreens() {
            try {
                const { data } = await http.get(`/live-data/screens/${activeSite.id}`);

                // ?? If cached order exists, apply it
                const cachedOrder = localStorage.getItem(`screensOrder_${user?.ID}`);
                if (cachedOrder) {
                    const order = JSON.parse(cachedOrder);
                    // reorder fetched data based on cached order
                    const orderedData = order.map(id => data.find(s => s.ID === id)).filter(Boolean);
                    setScreens(orderedData.length ? orderedData : data);
                } else {
                    setScreens(data);
                }
            } catch {
                // ignore error
            }
        }

        if (activeSite.id) getScreens();
    }, [activeSite, user]);

    const handleScreenChange = (screen) => {
        if (activeScreen.Type !== "DLY" && screen.Type === "DLY")
            setActiveDateRange(defaultDailyDateRange);
        if (activeScreen.Type !== "HLY" && screen.Type === "HLY")
            setActiveDateRange(defaultHourlyDateRange);
        setActiveScreen(screen);
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const reordered = Array.from(screens);
        const [removed] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, removed);
        setScreens(reordered); // update state with new order
        // ?? Save new order in cache (per user)
        if (user) {
            const orderIds = reordered.map(s => s.ID);
            localStorage.setItem(`screensOrder_${user.ID}`, JSON.stringify(orderIds));
        }
        setActiveScreen(activeScreen);
    };

    // let filteredScreens = [...screens].filter(
    //   (s) => s.type === operation || s.type === "ALL"
    // );

    function handleDateChange({ currentTarget: input }) {
        setDate(moment(input.value));
    }

    return (
        <div className={sidebar ? "col-10 pt-4" : "col-12 pt-4"}>
            <div className="container">
                <div className="row">
                    <div className="col-6">
                        <ListGroup
                            items={sites}
                            onItemSelect={(site) => {
                                setActiveSite(site);
                                setActiveScreen({});
                            }}
                            selectedItem={activeSite}
                        />
                    </div>
                    <div className="col-3">
                        <ListGroup
                            items={activeScreen.Type === "DLY" ? dailyDateRanges : dateRanges}
                            onItemSelect={(range) => {
                                setActiveDateRange(range);
                            }}
                            selectedItem={activeDateRange}
                        />
                    </div>
                    <div className="col-2 ml-auto">
                        <SelectDate
                            value={date.format("YYYY-MM-DD")}
                            onChange={handleDateChange}
                        />
                    </div>
                </div>
            </div>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="navbar" direction="horizontal">
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                            <LiveNavbar
                                items={screens}
                                active={activeScreen}
                                navClick={handleScreenChange}
                                droppableProvided={provided} // pass droppable context down
                            />
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            <div className="container mt-1">
                <div className="row pt-2">
                    <div className="col-2">
                        <button className="btn btn-primary" onClick={() => setAsc(!asc)}>
                            Toggle Sort{" "}
                            {asc ? (
                                <i className="fas fa-arrow-down" />
                            ) : (
                                <i className="fas fa-arrow-up" />
                            )}
                        </button>
                    </div>
                </div>
            </div>
            <div className="container-fluid row">
                <div className="col">
                    <Page
                        site={activeSite}
                        date={date}
                        screen={activeScreen}
                        dateRange={activeDateRange}
                        sorting={asc}
                    />
                </div>
            </div>
        </div>
    );
};

const Page = ({ site, date, screen, dateRange, sorting }) => {
    const [user] = useContext(UserContext);
    const dataTickInterval = 3 * 60000;

    const [headers, setHeaders] = useState([]);
    const [data, setData] = useState([]);
    const [hour, setHour] = useState(new Date().getHours());
    // const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [chartCodes, setChartCodes] = useState([]);

    const [chartData, setChartData] = useState([]);

    const [showSub, setShowSub] = useState(false);
    const [subscription, setSubscription] = useState();
    const [subInfo, setSubInfo] = useState();

    useEffect(() => {
        async function getHeaders() {
            try {
                const { data } = await http.get(`/live-data/heading/${site.id}/${screen.ID}`);
                setHeaders(data);
            }
            catch {
                //ignore error
            }
        }

        if (site.id && screen.ID) {
            getHeaders();
        }
        if (!screen.ID) setHeaders([]);
    }, [site, screen]);

    const getData = useCallback(async () => {
        setLoading(true);
        const apiDate = moment(date).format("YYYY-MM-DD");

        try {
            const { data } = await http.get(`/live-data/hourly-data/${site.id}/${screen.ID}/${apiDate}/${hour}/${dateRange.id}`);
            setHour(new Date().getHours());
            setData(data);
        }
        catch {
            //ignore error
        }

        setLoading(false);
    }, [site, date, screen, dateRange, hour]);

    useEffect(() => {
        let timerId;
        if (site.id && screen.ID && dateRange.id) {
            getData();
            timerId = setInterval(() => {
                getData();
            }, dataTickInterval);
        }
        if (!screen.ID) setData([]);
        return () => {
            clearInterval(timerId);
        };
    }, [site, screen, dateRange, dataTickInterval, getData]);

    useEffect(() => {
        if (chartCodes.length === 0) return setChartData([]);

        const newChartData = chartCodes.map((Code) => {
            const infoIndex = headers.findIndex((header) => header.CODE === Code);
            if (infoIndex === -1)
                return { name: Code, upperCheck: null, lowerCheck: null, data: [] };
            const codeData = data
                .filter((record) => !!record[Code])
                .map((filt) => {
                    return {
                        x: moment(filt.PARAM_DATE).add(filt.HOUR, "hours").valueOf(),
                        y: filt[Code],
                    };
                });
            return {
                name: Code,
                upperCheck: headers[infoIndex].UPPER_CHECK,
                lowerCheck: headers[infoIndex].LOWER_CHECK,
                data: codeData,
            };
        });
        setChartData(newChartData);
    }, [data, chartCodes, headers]);

    const handleChart = (header) => {
        const { CODE: Code } = header;
        setChartCodes((codes) => [...codes, Code]);
    };

    const handleShowSubscribe = (header) => {
        setSubscription({
            code: header.CODE,
            type: "",
            site: user.SITE,
            email: false,
            push: false,
        });
        setSubInfo({
            UPPER_ABS_LIMIT: header.UPPER_ABSOLUTE,
            UPPER_CHK_LIMIT: header.UPPER_CHECK,
            LOWER_CHK_LIMIT: header.LOWER_CHEDK,
            LOWER_ABS_LIMIT: header.LOWER_ABSOLUTE,
        });
        setShowSub(true);
    };

    const subChange = ({ currentTarget: input }) => {
        const newData = { ...subscription };
        const value = input.type === "checkbox" ? input.checked : input.value;
        newData[input.name] = value;
        setSubscription(newData);
    };

    return (
        <div>
            <LiveChart data={chartData} handleHideChart={() => setChartCodes([])} />
            <div className="row">
                {loading ? <Loading /> : null}
                <DataTable
                    data={data}
                    headers={headers}
                    headerClick={handleChart}
                    sorting={sorting}
                    subscribeClick={handleShowSubscribe}
                />
            </div>
            {showSub ? (
                <ParameterSubscribe
                    show={showSub}
                    handleChange={subChange}
                    handleHide={() => setShowSub(!showSub)}
                    data={subscription}
                    info={subInfo}
                />
            ) : null}
        </div>
    );
};

export default LiveData;
